import * as React from "react";
import { logger } from "bernie-core";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { inject } from "mobx-react";
import { CustomerNotifications } from "components/shared/CustomerNotifications";
import { CustomerNotificationLocation } from "@shared-ui/customer-notifications";
import { CustomerNotificationsWithDialogWrapper } from "src/components/shared/CustomerNotifications/CustomerNotificationsWithDialogWrapper";
import { StorefrontWizardRegionBEXProps } from "./typings";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { withLocalization } from "@shared-ui/localization-context";
import { LobQueryRouter } from "src/components/shared/StorefrontWizard/LobQueryRouter";
import {
  emitUserInteractionDomainEvent,
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
  getNonWizardFormChildren,
} from "src/components/shared/StorefrontWizard/StorefrontWizardUtils";
import { TabbedWizardCard } from "src/components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { LobFormWithTabs } from "src/components/shared/StorefrontWizard/LobFormWithTabs/LobFormWithTabs";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { ReasonsToBelieveWrapper } from "src/components/shared/ReasonsToBelieve/ReasonsToBelieveWrapper";
import { Experiment as BernieExperiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { getTracker } from "src/utils/TrackingUtils";

@withLocalization
@logger("StorefrontWizardRegionBEX")
@inject("wizardState", "analytics", "thumbor", "page")
export class StorefrontWizardRegionBEX extends React.Component<StorefrontWizardRegionBEXProps> {
  public componentDidMount() {
    if (isVariantEnabled(this.props.context, Experiment.EG_UIDE_SDK_SF_test)) {
      emitUserInteractionDomainEvent();
    }
  }

  public render() {
    const flexComponent = this.props.flexComponent;
    const context = this.props.context;
    const wizardState = this.props.wizardState;
    const analytics = this.props.analytics;
    const blossomComponent = BlossomComponentFactory.get(this.props as BlossomComponentFactoryProps);

    //default to false if device information is not available
    wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

    if (!flexComponent || !flexComponent.model || !blossomComponent) {
      return null;
    }

    wizardState.globalWizardState.config.useLastSearchForMultiLOB = flexComponent.model.useLastSearchForMultiLOB;

    if (flexComponent.model.defaultLOB) {
      wizardState.globalWizardState.config.navigation.defaultLob = flexComponent.model.defaultLOB;
      wizardState.globalWizardState.config.navigation.activeLob = flexComponent.model.defaultLOB;
    }

    wizardState.globalWizardState.setLastSearchedLob();

    const children = blossomComponent.renderChildren(this.props.logger, this.props.l10n, context);

    if (children.length < 1) {
      return null;
    }

    const wizardLOBs = getWizardLOBFlexComponents(flexComponent.modules);

    const wizardFormChildren = getWizardLOBFormChildren(children);

    const otherFormChildren = getNonWizardFormChildren(children);

    const { heroImageSrc } = wizardState.globalWizardState.config;

    const imageSrcTemp = this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          heroImageSrc,
          1400,
          600,
          context,
          AspectRatioEnum.R_21_9,
          true,
          80
        )
      : heroImageSrc;

    const isStorefrontPage = context.searchContext.pageType.indexOf("Storefront") > -1;
    const reasonsToBelieveOn = isVariantEnabled(
      this.props.context,
      Experiment.Surface_PWA_SF_Travel_Advisory_Chatbot,
      2
    );
    const wizardRegionClassNames = `StorefrontWizardRegionBEX ${reasonsToBelieveOn ? "" : "all-b-margin-six"}`;
    const heroImageFigureClassNames = `bexHeroImageFigure ${reasonsToBelieveOn ? "withBackgroundColor" : ""}`;

    const lobNameMap = {
      "wizard-hotel-pwa-v2": "_Hotels",
      "wizard-flight-pwa": "_Flights",
      "wizard-package-pwa": "_Packages",
      "wizard-car-pwa": "_Cars",
      "wizard-lx-pwa": "_Lx",
      "wizard-cruise-pwa": "_Cruise",
    };

    const trackOnChangeTab = (tabName: string) => {
      const moduleName = flexComponent.name === "" ? "SFWizardBEX" : flexComponent.name;
      const tracker = getTracker(wizardState.canTrack, analytics, moduleName);
      const lobName = lobNameMap[tabName];
      const rfrr = `LOB_Selection_Occured${lobName ?? ""}`;
      tracker.trackEvent(rfrr);
    };

    /**
     *
     * Note for modules below this region:
     * Modules not part of this region do not align to the form content out of the box
     * Setting the flex manager attribute brandModulePadding to bex_padding puts a spacing__twelve on the components below this region
     * This aligns them with the form content within the card on this wizard
     * On mobile, there is no card, and this same attribute puts spacing__six on these components
     */
    return (
      <>
        <StorefrontWizardRegionTypesContext.Provider value={StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_BEX}>
          <div className={wizardRegionClassNames} data-testid="storefront-region">
            <div data-testid="main-region">
              <Viewport>
                <ViewSmall>
                  <>
                    <LobQueryRouter lobs={wizardLOBs} />
                    {isStorefrontPage && (
                      <CustomerNotifications
                        location={CustomerNotificationLocation.AFTER_HEADER}
                        className="customerNotificationSquare"
                      />
                    )}
                    <LobFormWithTabs
                      context={context}
                      lobForms={wizardFormChildren}
                      wizardFormClassNames="all-x-padding-six"
                      tabGroupClassNames="all-l-padding-six"
                      trackOnChangeTab={trackOnChangeTab}
                    />
                  </>
                </ViewSmall>

                <ViewMedium>
                  <UitkFigure className={heroImageFigureClassNames}>
                    <UitkImage alt="" src={imageSrcTemp} className="bexHeroImageBackground" />
                    <LobQueryRouter lobs={wizardLOBs} />
                    {isStorefrontPage && (
                      <CustomerNotifications
                        location={CustomerNotificationLocation.AFTER_HEADER}
                        className="customerNotificationSquare all-x-padding-six all-t-padding-twelve"
                      />
                    )}
                    <TabbedWizardCard
                      context={context}
                      wizardFormChildren={wizardFormChildren}
                      wizardState={wizardState}
                      wrappingClassNames="all-t-padding-twelve all-x-padding-six SimpleContainer"
                      cardClassNames="all-cell-1-1 elevation-4"
                      cardContentClassNames="uitk-cell lobFormInCard"
                      tabGroupClassNames="formAlignedTabs all-x-padding-six"
                      wizardFormClassNames="wizardOverHeroImage all-x-padding-six"
                      trackOnChangeTab={trackOnChangeTab}
                    />
                  </UitkFigure>
                </ViewMedium>
              </Viewport>
              {otherFormChildren}
            </div>
          </div>
          <BernieExperiment name={"Surface_PWA_SF_Travel_Advisory_Chatbot"}>
            <ExperimentControl />
            <ExperimentVariant bucket={1}>
              <CustomerNotificationsWithDialogWrapper
                location="IN_PAGE"
                className="customerNotificationSquare customerNotificationWithDialog all-x-padding-six"
              />
            </ExperimentVariant>
            <ExperimentVariant bucket={2}>
              <ReasonsToBelieveWrapper location="IN_PAGE" className="reasons-to-believe" />
            </ExperimentVariant>
          </BernieExperiment>
        </StorefrontWizardRegionTypesContext.Provider>
      </>
    );
  }
}

export default StorefrontWizardRegionBEX;
